import { useMemo } from "react";
import { useMutation } from "@tanstack/react-query";

import {
  generateId,
  useCareSchedulesData,
  useOwnerId,
  type PlantCareSchedule,
} from "~/state/sync";
import { mk, type MutationVars } from "~/state/mutations";
import type { CareTaskKind } from "~/api/types";

function addDays(iso: string, days: number) {
  const date = new Date(iso);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString();
}

export function usePlantCareSchedules(plantId: string | undefined) {
  const { data } = useCareSchedulesData();
  return useMemo(() => {
    if (!plantId) return [] as PlantCareSchedule[];
    return (data ?? []).filter((schedule) => schedule.plant_id === plantId);
  }, [data, plantId]);
}

export function useCareActions() {
  const userId = useOwnerId();
  const { data } = useCareSchedulesData();
  const createM = useMutation<unknown, Error, MutationVars["scheduleCreate"]>({
    mutationKey: [...mk.scheduleCreate],
  });
  const updateM = useMutation<unknown, Error, MutationVars["scheduleUpdate"]>({
    mutationKey: [...mk.scheduleUpdate],
  });
  const removeM = useMutation<unknown, Error, MutationVars["scheduleRemove"]>({
    mutationKey: [...mk.scheduleRemove],
  });

  function requireUser() {
    if (!userId) throw new Error("You must be signed in.");
    return userId;
  }

  function upsertSchedule(
    plantId: string,
    input: { taskKind: CareTaskKind; intervalDays: number; enabled?: boolean },
  ) {
    const uid = requireUser();
    const intervalDays = Math.max(1, Math.trunc(input.intervalDays) || 1);
    const existing = (data ?? []).find(
      (schedule) =>
        schedule.plant_id === plantId && schedule.task_kind === input.taskKind,
    );
    const now = new Date().toISOString();

    if (existing) {
      const fields: Partial<PlantCareSchedule> = {
        interval_days: intervalDays,
      };
      if (input.enabled !== undefined) fields.enabled = input.enabled;
      updateM.mutate({ userId: uid, id: existing.id, fields });
      return existing.id;
    }

    const id = generateId();
    const row: PlantCareSchedule = {
      id,
      plant_id: plantId,
      owner_id: uid,
      task_kind: input.taskKind,
      interval_days: intervalDays,
      next_due_at: addDays(now, intervalDays),
      last_completed_at: null,
      enabled: input.enabled ?? true,
      created_at: now,
      updated_at: now,
      deleted: false,
    };
    createM.mutate({ userId: uid, row });
    return id;
  }

  function completeTask(scheduleId: string) {
    const uid = requireUser();
    const schedule = (data ?? []).find((s) => s.id === scheduleId);
    if (!schedule) return;
    const now = new Date().toISOString();
    updateM.mutate({
      userId: uid,
      id: scheduleId,
      fields: {
        last_completed_at: now,
        next_due_at: addDays(now, schedule.interval_days),
      },
    });
  }

  function setEnabled(scheduleId: string, enabled: boolean) {
    const uid = requireUser();
    updateM.mutate({ userId: uid, id: scheduleId, fields: { enabled } });
  }

  function remove(scheduleId: string) {
    const uid = requireUser();
    removeM.mutate({ userId: uid, id: scheduleId });
  }

  return { upsertSchedule, completeTask, setEnabled, remove };
}
